import { NavLink } from 'react-router-dom'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchProjects } from '../store/slices/projectSlice'
import useSystemSettings from '../hooks/useSystemSettings'
import {
  HomeIcon,
  FolderIcon,
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline'
import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const dispatch = useAppDispatch()
  const { projects, loading: projectsLoading } = useAppSelector(state => state.project)
  const { user, isAuthenticated } = useAppSelector(state => state.auth)
  const { getSetting } = useSystemSettings()
  const [collapsed, , toggleCollapsed] = useSidebarCollapsed()

  const sidebarTitle = getSetting('system.sidebar_title', 'MoniJira')
  const sidebarLogo = getSetting('system.sidebar_logo', '')

  // 若 sidebar 掛載時 projects 還沒載入（例如直接以深連結進入某個專案子頁），
  // 主動補抓一次；否則「專案」清單與「財務管理」頂層捷徑都會是空的，
  // 直到使用者點到其他頁面才觸發 fetchProjects 才會出現。
  useEffect(() => {
    if (isAuthenticated && user && !projectsLoading && projects.length === 0) {
      dispatch(fetchProjects())
    }
  }, [dispatch, isAuthenticated, user, projectsLoading, projects.length])

  const hasFinanceEnabledProject = projects.some(p => p.financeEnabled)

  const navigation = [
    { name: '儀表板', href: '/', icon: HomeIcon },
    { name: '搜尋', href: '/search', icon: MagnifyingGlassIcon },
    { name: '自訂表單', href: '/forms/templates', icon: ClipboardDocumentListIcon },
    { name: '日曆', href: '/calendar', icon: CalendarIcon },
    { name: '全域工作日誌', href: '/global-worklogs', icon: DocumentTextIcon },
    { name: '統計報表', href: '/reports', icon: ChartBarIcon },
    ...(hasFinanceEnabledProject ? [{ name: '財務管理', href: '/finance', icon: BanknotesIcon }] : []),
  ]

  // 管理員導航項目
  const adminNavigation = (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') ? [
    { name: '管理員後台', href: '/admin', icon: ShieldCheckIcon },
    { name: '專案管理', href: '/admin/projects', icon: BuildingOfficeIcon },
  ] : []

  // isCompact: 桌面版收合後只顯示 icons（手機版 Drawer 永遠是展開狀態）
  const SidebarContent = ({ isCompact = false }: { isCompact?: boolean }) => (
    <div className="flex flex-col h-full">
      <div className={isCompact ? 'px-2 py-4' : 'p-6'}>
        <div className={`flex items-center ${isCompact ? 'justify-center' : 'justify-between'}`}>
          {!isCompact && (
            <div className="flex items-center space-x-3 min-w-0">
              {sidebarLogo && (
                <img
                  src={sidebarLogo.startsWith('http') ? sidebarLogo : `${window.location.origin}${sidebarLogo}`}
                  alt="側邊欄 Logo"
                  className="h-8 w-auto object-contain flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              )}
              <h2 className="text-lg font-semibold text-gray-900 truncate">{sidebarTitle}</h2>
            </div>
          )}
          <button
            className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          {/* 桌面版收合/展開切換 */}
          <button
            type="button"
            onClick={toggleCollapsed}
            className="hidden md:inline-flex p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            title={isCompact ? '展開側邊欄' : '收合側邊欄'}
          >
            {isCompact ? <ChevronDoubleRightIcon className="h-5 w-5" /> : <ChevronDoubleLeftIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <nav className={`flex-1 ${isCompact ? 'px-2' : 'px-4'} space-y-1 overflow-y-auto`}>
        {[...navigation, ...adminNavigation].map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/' || item.href === '/admin'}
            onClick={onClose}
            title={isCompact ? item.name : undefined}
            className={({ isActive }) =>
              `group flex items-center ${isCompact ? 'justify-center px-2' : 'px-2'} py-2 text-sm font-medium rounded-md ${
                isActive
                  ? 'bg-primary-100 text-primary-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <item.icon className={`h-5 w-5 ${isCompact ? '' : 'mr-3'}`} />
            {!isCompact && item.name}
          </NavLink>
        ))}

        <div className="pt-4">
          {!isCompact && (
            <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              專案
            </h3>
          )}
          <div className={`${isCompact ? '' : 'mt-2'} space-y-1`}>
            {projects.map((project) => (
              <div key={project.id} className="space-y-1">
                <NavLink
                  to={`/project/${project.key}/board`}
                  onClick={onClose}
                  title={isCompact ? `${project.name}（${project.key}）` : undefined}
                  className={({ isActive }) =>
                    `group flex items-center ${isCompact ? 'justify-center px-2' : 'px-2'} py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  <FolderIcon className={`h-5 w-5 ${isCompact ? '' : 'mr-3'}`} />
                  {!isCompact && (
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{project.name}</span>
                      <span className="block text-xs text-gray-500">{project.key}</span>
                    </div>
                  )}
                </NavLink>
                {/* Finance link for each project - only show if financeEnabled */}
                {project.financeEnabled && (
                  <NavLink
                    to={`/project/${project.key}/finance`}
                    onClick={onClose}
                    title={isCompact ? `${project.name} 財務管理` : undefined}
                    className={({ isActive }) =>
                      `group flex items-center ${isCompact ? 'justify-center px-2' : 'pl-10 pr-2'} py-1.5 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-green-100 text-green-900'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`
                    }
                  >
                    <BanknotesIcon className={`h-4 w-4 ${isCompact ? '' : 'mr-2'}`} />
                    {!isCompact && <span className="truncate">財務管理</span>}
                  </NavLink>
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>

      <div className={`${isCompact ? 'p-2' : 'p-4'} border-t border-gray-200`}>
        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
          <NavLink
            to="/settings"
            onClick={onClose}
            title={isCompact ? '系統設定' : undefined}
            className={`group flex items-center ${isCompact ? 'justify-center px-2' : 'px-2'} py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900`}
          >
            <CogIcon className={`h-5 w-5 ${isCompact ? '' : 'mr-3'}`} />
            {!isCompact && '系統設定'}
          </NavLink>
        )}
      </div>
    </div>
  )

  return (
    <>
      {/* 桌面版 Sidebar（可收合） */}
      <div
        className={`hidden md:flex md:flex-col transition-all duration-200 ease-in-out ${collapsed ? 'md:w-16' : 'md:w-64'}`}
      >
        <div className="bg-white border-r border-gray-200 flex flex-col flex-1">
          <SidebarContent isCompact={collapsed} />
        </div>
      </div>

      {/* 手機版 Sidebar Modal */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 md:hidden" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="bg-white flex flex-col flex-1">
                  <SidebarContent />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default Sidebar
